import ApiClient from '@afosto/api/ApiClient';

class Subscription {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Subscribe to an add on
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Add an add on to a subscription
   * @returns {Promise}
   */
  addAddOn(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/subscriptions/addons`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Merge a payment source to a user
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Add a payment source
   * @returns {Promise}
   */
  addPaymentSource(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/sources`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Subscribe to a service
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Add a product to the subscription
   * @returns {Promise}
   */
  addSubscription(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/subscriptions`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Obtain billing information
   *
   * @param {Object} options - The additional request options
   * @description Customer information regarding billing data
   * @returns {Promise}
   */
  getBillingInformation(options = {}) {
    return this.apiClient.request({
      url: `/iam/customer`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Obtain a live projection of the trial status
   *
   * @param {Object} options - The additional request options
   * @description Get information about the trial
   * @returns {Promise}
   */
  getTrial(options = {}) {
    return this.apiClient.request({
      url: `/iam/subscriptions/trial`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Obtain a live projection of the upcoming invoice
   *
   * @param {Object} query - The optional path parameters
   * @param {string} [query.code] - cnt
   * @param {Object} options - The additional request options
   * @description Get information about the next invoice
   * @returns {Promise}
   */
  getUpcoming(query = {}, options = {}) {
    const params = {
      code: query.code,
    };

    return this.apiClient.request({
      url: `/iam/subscriptions/invoices/next`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Obtain a live list of active subscriptions
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description List of subscribed products
   * @returns {Promise}
   */
  getUsages(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/subscriptions/usages`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Obtain a list of payment sources
   *
   * @param {Object} options - The additional request options
   * @description List of payment sources
   * @returns {Promise}
   */
  listPaymentSources(options = {}) {
    return this.apiClient.request({
      url: `/iam/sources`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Obtain a list of products
   *
   * @param {Object} options - The additional request options
   * @description List products
   * @returns {Promise}
   */
  listProducts(options = {}) {
    return this.apiClient.request({
      url: `/iam/products`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Obtain a live list of active invoices
   *
   * @param {Object} options - The additional request options
   * @description List of the invoices
   * @returns {Promise}
   */
  listSubscriptionInvoices(options = {}) {
    return this.apiClient.request({
      url: `/iam/subscriptions/invoices`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Obtain a live list of active subscriptions
   *
   * @param {Object} options - The additional request options
   * @description List of subscribed products
   * @returns {Promise}
   */
  listSubscriptions(options = {}) {
    return this.apiClient.request({
      url: `/iam/subscriptions`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Remove a payment source from the user
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Remove an active payment source
   * @returns {Promise}
   */
  removePaymentSource(id, options = {}) {
    return this.apiClient.request({
      url: `/iam/sources/${id}`,
      method: 'delete',
      ...options,
    });
  }

  /**
   * Update billing information
   *
   * @param {Object} body - Email object
   * @param {Object} options - The additional request options
   * @description Update customer information regarding billing data
   * @returns {Promise}
   */
  updateBillingInformation(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/customer`,
      method: 'put',
      data: body,
      ...options,
    });
  }
}

export const subscription = new Subscription();

export default Subscription;
